/**
 * Internal Cancel-Pending API Contract
 *
 * POST /internal/platform-data-sync/cancel-pending — cancel redundant PENDING sync jobs
 *
 * API-key-gated (`INTERNAL_API_KEY`). The operator path for the thing that had
 * to be done with hand-written `UPDATE`s against production on 2026-09-02: a
 * duplicate-parent storm minted thousands of redundant `PlatformDataSyncJob`
 * rows, and nothing in the API could retire them.
 *
 * INVARIANTS the contract commits to:
 *  - ONLY `status = 'PENDING'` rows are ever written. An IN_PROGRESS row is
 *    never touched — a worker is holding it, and the dispatch terminal-status
 *    guard already acks a CANCELLED row in milliseconds, so cancelling by DB
 *    status is sufficient and no SQS purge is needed.
 *  - `dryRun` DEFAULTS TO TRUE. A caller that forgets the flag gets a report.
 *  - Applying is idempotent: the write re-asserts `status = 'PENDING'`, so a
 *    second apply of the same request cancels 0.
 */

import { z } from 'zod'

/**
 * Which redundancy shape to retire.
 *
 * - `duplicate-parents`: among the PENDING PARENT rows (`parent_job_id IS NULL`)
 *   for one (school, syncType) inside a ROLLING window, keep the EARLIEST and
 *   cancel every later sibling. This is the 2026-09-02 shape — each scheduler
 *   tick minted another parent for the same school+type.
 * - `superseded-children`: PENDING CHILD rows whose PARENT has been overtaken —
 *   a later sibling of the parent (same school, same parent syncType, greater
 *   `created_at`) is SUCCESS / IN_PROGRESS / PENDING, so the child's fan-out
 *   work is already obsolete. `syncType` names the PARENT's type (e.g.
 *   `SCHOOL_USER_PROFILES_BULK`), not the child's.
 * - `by-ids`: exactly the rows named in `ids`, and only those still PENDING.
 */
export const CancelPendingModeSchema = z.enum(['duplicate-parents', 'superseded-children', 'by-ids'])

export type CancelPendingMode = z.infer<typeof CancelPendingModeSchema>

/** Hard ceiling on how many rows one call may retire, and on `ids` length. */
export const CANCEL_PENDING_MAX_MATCH = 5000

/** How many publicIds the response carries before it reports `truncated`. */
export const CANCEL_PENDING_MAX_REPORTED_IDS = 500

/**
 * Request body.
 *
 * `olderThanMinutes` is the LENGTH OF THE ROLLING LOOK-BACK WINDOW, not a
 * minimum age: the grouping modes consider rows created within the last N
 * minutes. It deliberately is NOT a calendar-day bound — the hand-written
 * incident queries used `created_at >= CURDATE()` and therefore missed
 * post-midnight duplicates whose sibling was created the previous evening.
 * Default is the reaper's `stuckQueuedMinutes`, so "still in flight" means the
 * same thing here as it does to the reaper and the scheduler's dedup guard.
 */
export const CancelPendingRequestSchema = z
  .object({
    mode: CancelPendingModeSchema.describe('Which redundancy shape to retire'),
    syncType: z
      .string()
      .min(1)
      .optional()
      .describe('REQUIRED for the grouping modes. For superseded-children this is the PARENT sync type'),
    schoolId: z
      .union([z.string().min(1), z.number().int().positive()])
      .optional()
      .describe('Optional school public ID (CUID), slug, or internal numeric id; omit to sweep the fleet'),
    olderThanMinutes: z
      .number()
      .int()
      .positive()
      .max(43_200)
      .optional()
      .describe("Rolling look-back window length in minutes (default: the reaper's stuckQueuedMinutes)"),
    ids: z
      .array(z.string().min(1))
      .min(1)
      .max(CANCEL_PENDING_MAX_MATCH)
      .optional()
      .describe('REQUIRED for by-ids: job public IDs (CUID) and/or numeric job ids'),
    dryRun: z.boolean().default(true).describe('Report without writing. DEFAULTS TO TRUE'),
    reason: z.string().max(2000).optional().describe('Audit reason, embedded in each cancelled row’s error column')
  })
  .superRefine((value, ctx) => {
    if (value.mode === 'by-ids') {
      if (!value.ids?.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ids'], message: 'ids is required for mode by-ids' })
      }
      return
    }
    if (!value.syncType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['syncType'],
        message: `syncType is required for mode ${value.mode}`
      })
    }
  })

export type CancelPendingRequest = z.infer<typeof CancelPendingRequestSchema>

/**
 * Per-(school, syncType) breakdown. `kept` is only meaningful where the mode
 * makes a keep-vs-cancel choice within a sibling set (`duplicate-parents`), so
 * it is omitted for the other modes rather than reported as a misleading zero.
 */
export const CancelPendingGroupSchema = z.object({
  schoolId: z.string().describe("School's public ID"),
  syncType: z.string().describe('Sync type of the rows in this group'),
  kept: z.number().int().nonnegative().optional().describe('Siblings deliberately left PENDING (duplicate-parents)'),
  cancelled: z.number().int().nonnegative().describe('Rows cancelled (or, on a dry run, that would be)')
})

export type CancelPendingGroup = z.infer<typeof CancelPendingGroupSchema>

/**
 * Response. `matched` is what the selection found; `cancelled` is what the
 * guarded write actually changed — on a dry run it is always 0, and on an apply
 * it can be lower than `matched` when a worker claimed a row in between.
 * `ids` are the MATCHED rows' public ids.
 */
export const CancelPendingResponseSchema = z.object({
  dryRun: z.boolean(),
  mode: CancelPendingModeSchema,
  matched: z.number().int().nonnegative(),
  cancelled: z.number().int().nonnegative(),
  ids: z.array(z.string()).describe(`Matched job public IDs, capped at ${CANCEL_PENDING_MAX_REPORTED_IDS}`),
  truncated: z.boolean().describe('True when more rows matched than `ids` carries'),
  byGroup: z.array(CancelPendingGroupSchema).optional()
})

export type CancelPendingResponse = z.infer<typeof CancelPendingResponseSchema>
